import { useState } from "react";
import { Plus, X, Play, BookOpen, Sun, Moon } from "lucide-react";
import { Avatar, Switch, Segmented } from "../ui";
import Scoreboard from "./Scoreboard";

export default function Menu({ settings, set, words, cats, onStart, onWords, board, onResetBoard }) {
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const players = settings.players;
  const minPlayers = settings.impostores === 2 ? 5 : 3;

  const add = (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (players.length >= 12) return setErr("Máximo 12 jugadores.");
    if (players.some((p) => p.toLowerCase() === n.toLowerCase())) return setErr("Ese nombre ya está en la lista.");
    set({ players: [...players, n] });
    setName("");
    setErr("");
  };

  const toggleCat = (c) => set({ categorias: cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c] });

  let reason = "";
  if (!words) reason = "Cargando palabras…";
  else if (players.length < minPlayers) reason = `Se necesitan al menos ${minPlayers} jugadores${settings.impostores === 2 ? " para 2 impostores" : ""}.`;
  else if (!cats.length) reason = "Selecciona al menos una categoría.";

  return (
    <div className="screen">
      <header className="hero">
        <div className="hero-top">
          <span className="eyebrow">Juego de deducción</span>
          <button className="icon-btn" onClick={() => set({ theme: settings.theme === "dark" ? "light" : "dark" })} aria-label="Cambiar tema" data-testid="theme-toggle-btn">
            {settings.theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
        <h1 className="title">El <span className="glitch-text" data-text="Impostor">Impostor</span></h1>
        <p className="subtitle">Todos conocen la palabra… menos uno.</p>
      </header>

      {board.games > 0 && <Scoreboard board={board} onReset={onResetBoard} />}

      <section className="glass section" data-testid="players-section">
        <div className="section-head">
          <h2>Jugadores</h2>
          <span className="badge" data-testid="players-count">{players.length}/12</span>
        </div>
        <form className="add-row" onSubmit={add}>
          <input className="input" value={name} maxLength={20} placeholder="Nombre del jugador" onChange={(e) => setName(e.target.value)} data-testid="player-name-input" />
          <button className="btn btn-primary btn-square" type="submit" aria-label="Agregar jugador" disabled={players.length >= 12} data-testid="add-player-btn"><Plus size={24} /></button>
        </form>
        {err && <p className="error" data-testid="player-error">{err}</p>}
        <ul className="player-list">
          {players.map((p, i) => (
            <li key={p} className="player-chip" data-testid={`player-item-${i}`}>
              <Avatar name={p} size="sm" />
              <span className="grow">{p}</span>
              <button className="icon-btn small" onClick={() => set({ players: players.filter((x) => x !== p) })} aria-label={`Quitar ${p}`} data-testid={`remove-player-${i}`}><X size={18} /></button>
            </li>
          ))}
        </ul>
      </section>

      <section className="glass section">
        <h2>Impostores</h2>
        <Segmented testid="impostors-count" value={settings.impostores} onChange={(v) => set({ impostores: v })}
          options={[{ value: 1, label: "1 impostor" }, { value: 2, label: "2 impostores" }]} />
        <h2 className="mt">El impostor recibe</h2>
        <Segmented testid="impostor-mode" value={settings.modo} onChange={(v) => set({ modo: v })}
          options={[{ value: "parecida", label: "Palabra parecida" }, { value: "ninguna", label: "Ninguna" }]} />
      </section>

      <section className="glass section" data-testid="categories-section">
        <div className="section-head">
          <h2>Categorías</h2>
          {words && (
            <button className="link-btn" onClick={() => set({ categorias: cats.length === words.categorias.length ? [] : words.categorias.map((c) => c.nombre) })} data-testid="toggle-all-categories-btn">
              {cats.length === words.categorias.length ? "Ninguna" : "Todas"}
            </button>
          )}
        </div>
        <div className="chips">
          {words?.categorias.map((c, i) => (
            <button key={c.nombre} className={`chip${cats.includes(c.nombre) ? " active" : ""}`} onClick={() => toggleCat(c.nombre)} data-testid={`category-chip-${i}`}>
              {c.nombre} <small>{c.pares.length}</small>
            </button>
          ))}
        </div>
        <button className="btn btn-ghost full mt" onClick={onWords} data-testid="manage-words-btn"><BookOpen size={18} /> Gestionar palabras</button>
      </section>

      <section className="glass section">
        <h2>Opciones</h2>
        <Switch label="Temporizador por turno" checked={settings.timerOn} onChange={(v) => set({ timerOn: v })} testid="timer-switch" />
        {settings.timerOn && (
          <div className="stepper" data-testid="timer-stepper">
            <button className="btn btn-ghost btn-square" onClick={() => set({ timerSecs: Math.max(10, settings.timerSecs - 5) })} data-testid="timer-minus-btn">−</button>
            <span className="stepper-value" data-testid="timer-value">{settings.timerSecs} s</span>
            <button className="btn btn-ghost btn-square" onClick={() => set({ timerSecs: Math.min(180, settings.timerSecs + 5) })} data-testid="timer-plus-btn">+</button>
          </div>
        )}
        <Switch label="Sonidos" checked={settings.sound} onChange={(v) => set({ sound: v })} testid="sound-switch" />
      </section>

      <div className="actions">
        {reason && <p className="hint" data-testid="start-hint">{reason}</p>}
        <button className="btn btn-primary btn-xl full" disabled={!!reason} onClick={onStart} data-testid="start-game-btn"><Play size={22} /> Empezar partida</button>
      </div>
    </div>
  );
}
